/**
 * useOrgUnitSubtreeFilter Hook
 *
 * V73.2: 조직 필터 subtree 공통 훅
 *
 * 조직 드롭다운에서 상위조직 선택 시 하위조직 포함 필터링을 위한 공통 로직.
 * 각 페이지에서 반복되던 아래 패턴을 한 곳에 통합:
 * - getDescendantOrgUnitIds() 호출
 * - useMemo descendant 계산
 * - 드롭다운 옵션 트리 정렬 + 들여쓰기
 *
 * 상태(selectedOrgUnitId)는 페이지에서 관리하고, 이 훅은 순수 계산만 수행.
 *
 * @example
 * ```tsx
 * const { orgUnits } = useOrgUnitsPublic({ isActive: true });
 * const [orgUnitId, setOrgUnitId] = useState('all');
 * const { descendantOrgUnitIds, orgDropdownOptions } = useOrgUnitSubtreeFilter(orgUnits, orgUnitId);
 * ```
 */

import { useMemo } from 'react';
import { getDescendantOrgUnitIds } from '../utils/orgUnitUtils';

/**
 * 훅 입력에 필요한 최소 OrgUnit 필드
 *
 * useOrgUnitsPublic, useOrgUnits 모두 호환.
 */
interface OrgUnitLike {
  id: string;
  name: string;
  parentId?: string | null;
  status?: string;
  type?: string;
  path?: string;
  level?: number;
}

/**
 * 훅 반환 타입
 */
export interface UseOrgUnitSubtreeFilterReturn {
  /** 하위조직 포함 ID 배열. 전체 선택 시 undefined */
  descendantOrgUnitIds: string[] | undefined;
  /** 하위조직 포함 ID Set. 전체 선택 시 null */
  descendantOrgUnitIdsSet: Set<string> | null;
  /** 트리 순서 정렬 + company 제외된 드롭다운 옵션 */
  orgDropdownOptions: OrgUnitLike[];
  /** 전체 조직 선택 여부 */
  isAllSelected: boolean;
}

/**
 * 조직 필터 subtree 공통 훅
 *
 * @param orgUnits 조직 목록 (useOrgUnitsPublic 또는 useOrgUnits 결과)
 * @param selectedOrgUnitId 현재 선택된 조직 ID ('all' 또는 '' = 전체)
 * @returns descendant IDs, 드롭다운 옵션, 전체 선택 여부
 */
export function useOrgUnitSubtreeFilter(
  orgUnits: OrgUnitLike[],
  selectedOrgUnitId: string
): UseOrgUnitSubtreeFilterReturn {
  const isAllSelected = !selectedOrgUnitId || selectedOrgUnitId === 'all';

  // descendant 조직 ID 배열
  const descendantOrgUnitIds = useMemo(() => {
    if (isAllSelected || orgUnits.length === 0) return undefined;
    return getDescendantOrgUnitIds(selectedOrgUnitId, orgUnits as any);
  }, [selectedOrgUnitId, orgUnits, isAllSelected]);

  // descendant 조직 ID Set (FE 필터링용)
  const descendantOrgUnitIdsSet = useMemo(() => {
    if (!descendantOrgUnitIds) return null;
    return new Set(descendantOrgUnitIds);
  }, [descendantOrgUnitIds]);

  // 드롭다운 옵션: company 타입 제외, path 기준 정렬 → 계층 순서 보장
  const orgDropdownOptions = useMemo(() => {
    return [...orgUnits]
      .filter(ou => ou.type !== 'company')
      .sort((a, b) => String(a.path || '').localeCompare(String(b.path || '')));
  }, [orgUnits]);

  return {
    descendantOrgUnitIds,
    descendantOrgUnitIdsSet,
    orgDropdownOptions,
    isAllSelected,
  };
}
